import { useState, useCallback, useRef, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import routeUrlProvider, { NOTFOUND } from 'constants/route-paths';

const useLocalStorage = (key, initialValue) => {
  if (!key) {
    throw new Error('Key must be provided to persist to localStorage');
  }
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = window.localStorage?.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      return initialValue;
    }
  });
  const setValue = (value) => {
    try {
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      window.localStorage?.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
      throw new Error(error);
    }
  };

  return [storedValue, setValue];
};

const useAsyncError = () => {
  const history = useHistory();
  return useCallback(() => {
    history.push(routeUrlProvider.getForRoute(NOTFOUND));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

const useFocus = () => {
  const htmlElRef = useRef(null);
  const setFocus = () => {
    htmlElRef.current && htmlElRef.current.focus();
  };

  return [htmlElRef, setFocus];
};

function usePrevious(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  }, [value]);

  return ref.current;
}

export { useLocalStorage, useAsyncError, useFocus, usePrevious };
